import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import TestDriveSetupSidebar from './TestDriveSetupSidebar';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_SETUP_INFO_HELP = `
  <div>
    <p><strong>Purpose: </strong>This helps you consider and keep track of the positioning for this setup. 
    That way you can replicate it later if you want to.
    Or compare it to other Test-drives which may have used a different positionining.
    </p>
    <p>Here you can make a note of the positioning for the client, the access method, and anything else that seems relevant.
    If there are particular mounts or mounting locations, you could enter that here as well.
    </p>
    <p>
    Other setup notes might include: specific settings (like in the computer's accessibility settings), the particular products you're using, any additional pieces of equipment needed for this setup, etc.
    </p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  activeAccessMethodName: PropTypes.string,
  activeSetupSelectionMethod: PropTypes.string,
  activeSetupAccessories: PropTypes.arrayOf(PropTypes.string),
  activeSetupBodySite: PropTypes.arrayOf(PropTypes.string),
  activeSetupName: PropTypes.string,
  activeSetupPositioningNotes: PropTypes.string,
  activeSetupOtherNotes: PropTypes.string,
  onChangeActiveSetupPositioningNotes: PropTypes.func.isRequired,
  onChangeActiveSetupOtherNotes: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  activeAccessMethodName: '',
  activeSetupSelectionMethod: '',
  activeSetupBodySite: [],
  activeSetupBodySiteComments: '',
  activeSetupAccessories: [],
  activeSetupPositioningNotes: '',
  activeSetupOtherNotes: '',
  activeSetupName: '',
};

//content sub-component with unique content for this page
class TestDriveSetupInfoContent extends Component {
  constructor(props){
    super(props);
  }

  //Render the form for asking about setup info.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
  render(){
    //these can be changed:
    const activeSetupPositioningNotes = this.props.activeSetupPositioningNotes;
    const activeSetupOtherNotes = this.props.activeSetupOtherNotes;
    const onChangeActiveSetupPositioningNotes = this.props.onChangeActiveSetupPositioningNotes;
    const onChangeActiveSetupOtherNotes = this.props.onChangeActiveSetupOtherNotes;
   
    return(
    <>
    <Form>
      <FormGroup>
        <Label for="positioning-notes" className="aa-bold-text">
          Positioning notes:
        </Label>
        <Input
          id="positioning-notes"
          value={activeSetupPositioningNotes}
          onChange={onChangeActiveSetupPositioningNotes}
          name="positioning-notes"
          type="textarea"
        />
      </FormGroup>
      <FormGroup>
        <Label for="other-notes" className="aa-bold-text">
          Other setup notes:
        </Label>
        <Input
          id="other-notes"
          value={activeSetupOtherNotes}
          onChange={onChangeActiveSetupOtherNotes}
          name="other-notes"
          type="textarea"
        />
      </FormGroup>
    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveSetupInfo(props) {
  //props for setup info section
  const activeAccessMethodName = props.activeAccessMethodName;
  const activeSetupSelectionMethod = props.activeSetupSelectionMethod;
  const activeSetupBodySite = props.activeSetupBodySite;
  const activeSetupAccessories = props.activeSetupAccessories;
  const activeSetupPositioningNotes = props.activeSetupPositioningNotes;
  const activeSetupOtherNotes = props.activeSetupOtherNotes;
  const activeSetupName = props.activeSetupName;
  const onChangeActiveSetupPositioningNotes = props.onChangeActiveSetupPositioningNotes;
  const onChangeActiveSetupOtherNotes = props.onChangeActiveSetupOtherNotes;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;

  const dev = props.dev || {};

  console.log("rendering TestDriveSetupInfo, with props: " + JSON.stringify(props));

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Enter positioning and any other setup needs`,
    subTitle: `Almost done with setup! Now note any other setup info you want to keep track of, like positioning for the access method and the client, 
      or particular settings or product names.`,
    learnMore: {linkText: "Learn More about positioning and other setup notes",
                title: "Positioning and other setup notes",
                content: TD_SETUP_INFO_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('TestDriveSetupInfo mounted');
    return () => {
      console.log('TestDriveSetupInfo unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveSetupInfoContent 
          activeSetupPositioningNotes={activeSetupPositioningNotes}
          activeSetupOtherNotes={activeSetupOtherNotes}
          onChangeActiveSetupPositioningNotes={onChangeActiveSetupPositioningNotes}
          onChangeActiveSetupOtherNotes={onChangeActiveSetupOtherNotes}
        />
      }
      sidebarComponent={
        dev.showDevSidebar 
        ?
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
        :  
        <TestDriveSetupSidebar 
          activeAccessMethodName={activeAccessMethodName}
          activeSetupSelectionMethod={activeSetupSelectionMethod}
          activeSetupBodySite={activeSetupBodySite}
          activeSetupAccessories={activeSetupAccessories}
          activeSetupName={activeSetupName}
       />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

TestDriveSetupInfo.propTypes = propTypes;
TestDriveSetupInfo.defaultProps = defaultProps;

export default TestDriveSetupInfo;